<script setup lang="ts">
  import { RadioGroup as ArkRadioGroup } from '@ark-ui/vue';
  import { computed, useId } from 'vue';
  import FormField from '../FormField/FormField.vue';
  import { rootClass, rootNoGapClass } from './RadioGroup.css';
  import {
    RadioGroupOrientation,
    type RadioGroupEmits,
    type RadioGroupProps,
  } from './RadioGroup.types';

  const props = withDefaults(defineProps<RadioGroupProps>(), {
    disabled: false,
    required: false,
    orientation: RadioGroupOrientation.horizontal,
    withGap: true,
  });

  const emit = defineEmits<RadioGroupEmits>();

  defineOptions({ inheritAttrs: false });

  const autoId = useId();
  const groupId = computed(() => props.id ?? autoId);

  const rootProps = computed(() => ({
    id: groupId.value,
    disabled: props.disabled,
    required: props.required,
    invalid: !!props.error,
    name: props.name,
    orientation: props.orientation,
    modelValue: props.modelValue,
    'onUpdate:modelValue': (value: string | null) => {
      emit('update:modelValue', value ?? '');
    },
  }));
</script>

<template>
  <FormField
    :id="groupId"
    :label="label"
    :error="error"
    :announce-warning="false"
    :disabled-reason="disabled ? disabledReason : undefined"
  >
    <template #default="{ describedBy, labelId }">
      <ArkRadioGroup.Root
        v-bind="rootProps"
        :ids="labelId ? { label: labelId } : undefined"
        :class="[rootClass, { [rootNoGapClass]: !withGap }]"
        :aria-describedby="describedBy"
      >
        <slot />
      </ArkRadioGroup.Root>
    </template>
  </FormField>
</template>
